import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, Button, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { PriceBreakdownTooltip } from "./StockCardTooltip";

const MobileCardOverlay = ({
  cardName,
  onPurchase,
  onSell,
  canPurchase,
  canSell,
  buyPrice,
  sellPrice,
  selectedActionCard = null,
  actionMode = null,
  onApplyAction = null,
  roi = null,
  onViewDetails = null,
  tooltipContent = null,
  description = null,
  isMyTurn = false,
  // Price breakdown props
  priceBreakdownProps = null,
}) => {
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Debug logging
  React.useEffect(() => {
    if (open) {
      console.log('[MobileCardOverlay]', {
        cardName,
        isMyTurn,
        canPurchase,
        canSell,
        hasOnPurchase: !!onPurchase,
        hasOnSell: !!onSell,
        buyPrice,
        sellPrice
      });
    }
  }, [open, isMyTurn, canPurchase, canSell, onPurchase, onSell, cardName, buyPrice, sellPrice]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePurchase = () => {
    if (onPurchase && canPurchase) {
      onPurchase();
      handleClose();
    }
  };

  const handleSell = () => {
    if (onSell && canSell) {
      onSell();
      handleClose();
    }
  };

  const handleApplyAction = () => {
    if (onApplyAction && selectedActionCard && actionMode) {
      onApplyAction();
      handleClose();
    }
  };

  const handleViewDetails = () => {
    setDetailsOpen(true);
    // Don't close the main menu
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  return (
    <>
      {/* Tap target - full card area */}
      <div
        onClick={handleOpen}
        className="mobile-card-overlay-trigger"
      />

      {/* Mobile Menu Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          className: "mobile-overlay-dialog"
        }}
      >
        <DialogContent className="p-0">
          {/* Header */}
          <div className="mobile-overlay-header">
            <h3 className="mobile-overlay-header-title">
              {cardName}
            </h3>
            <IconButton
              onClick={handleClose}
              size="small"
              className="p-xs"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {/* Stock Description */}
          {description && (
            <div className="mobile-overlay-description">
              {description}
            </div>
          )}

          {/* Actions */}
          <div className="mobile-overlay-actions">
            {/* Buy Button - Show if purchase handler exists (canPurchase already includes myTurn check) */}
            {onPurchase && (
              <Button
                onClick={handlePurchase}
                disabled={!canPurchase}
                fullWidth
                variant="contained"
                className={`mobile-overlay-button ${canPurchase ? 'mobile-overlay-button-buy' : 'mobile-overlay-button-disabled'}`}
              >
                <span className="mr-sm">⚡</span>
                BUY for ${buyPrice}
              </Button>
            )}

            {/* Sell Button - Show if sell handler exists (canSell already includes myTurn check) */}
            {onSell && (
              <Button
                onClick={handleSell}
                disabled={!canSell}
                fullWidth
                variant="contained"
                className={`mobile-overlay-button ${canSell ? 'mobile-overlay-button-sell' : 'mobile-overlay-button-disabled'}`}
              >
                <span className="mr-sm">⚡</span>
                SELL for ${sellPrice}
                {roi !== null && (
                  <span className={`ml-sm text-sm opacity-90 ${parseFloat(roi) >= 0 ? 'text-green-400' : 'text-pink-300'}`}>
                    ({parseFloat(roi) >= 0 ? '+' : ''}${roi})
                  </span>
                )}
              </Button>
            )}

            {/* Apply Action Card Button */}
            {selectedActionCard && actionMode && onApplyAction && (
              <Button
                onClick={handleApplyAction}
                disabled={!isMyTurn}
                fullWidth
                variant="contained"
                className={`mobile-overlay-button ${isMyTurn ? 'mobile-overlay-button-apply' : 'mobile-overlay-button-disabled'}`}
              >
                <span className="mr-sm">⚡</span>
                APPLY ACTION CARD
              </Button>
            )}

            {/* Show message if no actions available at all */}
            {!onPurchase && !onSell && !onApplyAction && (
              <div className="mobile-overlay-message">
                {isMyTurn 
                  ? 'No actions available for this card.'
                  : 'Wait for your turn to perform actions.'}
              </div>
            )}

            {/* Show Price Calculation Button */}
            {priceBreakdownProps && (
              <Button
                onClick={handleViewDetails}
                fullWidth
                variant="outlined"
                className="mobile-overlay-button-outlined"
              >
                <span className="mr-sm">📊</span>
                SHOW PRICE CALCULATION
              </Button>
            )}

            {/* Divider */}
            {(isMyTurn && (onPurchase || onSell || onApplyAction)) || priceBreakdownProps ? (
              <div className="mobile-overlay-divider" />
            ) : null}

            {/* Cancel Button */}
            <Button
              onClick={handleClose}
              fullWidth
              variant="text"
              className="mobile-overlay-button-text"
            >
              CANCEL
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        PaperProps={{
          className: "mobile-overlay-dialog"
        }}
      >
        <DialogContent className="p-lg">
          <div className="mobile-overlay-details-header">
            <h3 className="mobile-overlay-details-title">
              {cardName} Price Calculation
            </h3>
            <IconButton
              onClick={handleCloseDetails}
              size="small"
              className="p-xs"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          
          {/* Show thematic description if available */}
          {description && (
            <div className="mobile-overlay-details-description">
              {description}
            </div>
          )}
          
          {priceBreakdownProps ? (
            <div className="mobile-overlay-details-content">
              <PriceBreakdownTooltip {...priceBreakdownProps} />
            </div>
          ) : (
            <div className="mobile-overlay-details-content text-secondary">
              No price calculation available.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MobileCardOverlay;

